import { useState } from 'react';
import { Bell, Mail, MessageSquare } from 'lucide-react';
import { MeasurementTypeI18N } from '@the-real-insight/tri-model';

import { BRAND } from '@/brand/brand';

import { alertSampleContext, ALERT_TEXT_NAMESPACE, renderAlertTemplate } from './alertPlaceholders';
import { correctedMeasurementLabel } from './measurementLabelFix';

type Translate = (key: string, values?: Record<string, string>) => string;

/** Splits a template into plain text and `{{token}}` parts; the capture group keeps the tokens. */
const TAG_SPLIT = /(\{\{[^{}]*\}\})/;

/**
 * The template with sample data, the inserted values highlighted.
 *
 * Plain text and each token are rendered separately so the token's value can be marked; a template
 * with Mustache sections or triple braces cannot be cut apart safely and is rendered as one piece.
 * An unknown token renders empty (as in the alert) and is shown as an empty outlined gap.
 */
export function HighlightedTemplate(properties: { template?: string | null; testId?: string; language?: string; deviceAlarm?: string }) {
    const context = alertSampleContext(properties.language, properties.deviceAlarm);
    const template = properties.template ?? '';
    const cuttable = !/\{\{[#^/!>=&{]/.test(template) && !template.includes('{{{');

    if (!cuttable) {
        return <span data-testid={properties.testId}>{renderAlertTemplate(template, context)}</span>;
    }

    return (
        <span data-testid={properties.testId}>
            {template.split(TAG_SPLIT).map((part, index) => {
                if (part === '') {
                    return null;
                }

                if (!TAG_SPLIT.test(part)) {
                    return <span key={index}>{part}</span>;
                }

                const value = renderAlertTemplate(part, context);

                return (
                    <mark
                        key={index}
                        className={
                            value === ''
                                ? 'mx-0.5 inline-block min-w-3 rounded border border-dashed border-destructive/60 bg-transparent'
                                : 'rounded bg-primary/10 px-0.5 text-foreground'
                        }
                    >
                        {value}
                    </mark>
                );
            })}
        </span>
    );
}

/**
 * A phone lock screen with the push notification as it would appear: app mark and name, "now", the
 * headline and the text with the sample data. Light card on a dark screen whatever the portal theme,
 * because that is what the phone shows; the highlights keep the dark text of the card.
 */
function PhonePushPreview(properties: { t: Translate; title?: string; content?: string; language?: string; deviceAlarm?: string }) {
    const [markFailed, setMarkFailed] = useState(false);
    const date = new Date().toLocaleDateString(properties.language ?? 'de', { weekday: 'long', day: 'numeric', month: 'long' });

    return (
        <div
            data-testid="push-phone-preview"
            className="mx-auto flex w-full max-w-[260px] flex-col rounded-[2.5rem] border-[6px] border-neutral-900 bg-gradient-to-b from-slate-600 via-slate-800 to-slate-950 px-3 pb-8 pt-2.5 shadow-lg"
        >
            <div aria-hidden className="mx-auto h-5 w-20 rounded-full bg-black" />
            <div aria-hidden className="mt-4 flex flex-col items-center text-white">
                <span className="text-xs capitalize opacity-80">{date}</span>
                <span className="text-5xl font-light leading-none tracking-tight">9:41</span>
            </div>
            <div className="mt-5 flex min-w-0 flex-col gap-1 rounded-2xl bg-white/90 p-3 text-neutral-900 shadow">
                <div className="flex min-w-0 items-center gap-1.5 text-[11px] uppercase tracking-wide text-neutral-500">
                    {markFailed ? (
                        <span aria-hidden className="flex h-5 w-5 shrink-0 items-center justify-center rounded-md bg-neutral-800 text-[9px] font-semibold text-white">
                            {BRAND.fallbackInitials}
                        </span>
                    ) : (
                        <img
                            alt=""
                            src={BRAND.appIcon}
                            className="h-5 w-5 shrink-0 rounded-md object-contain"
                            onError={() => setMarkFailed(true)}
                        />
                    )}
                    <span className="min-w-0 truncate">{BRAND.name}</span>
                    <span className="ml-auto shrink-0 normal-case">{properties.t(`${ALERT_TEXT_NAMESPACE}pushNow`)}</span>
                </div>
                <div className="min-w-0 break-words text-sm font-semibold" data-testid="preview-title">
                    {properties.title}
                </div>
                <div
                    className="min-w-0 whitespace-pre-wrap break-words text-[13px] leading-snug [&_mark]:!text-neutral-900"
                    data-testid="preview-content"
                >
                    <HighlightedTemplate template={properties.content} language={properties.language} deviceAlarm={properties.deviceAlarm} />
                </div>
            </div>
        </div>
    );
}

/**
 * A small mail or SMS card with sample data — what the recipient would read.
 *
 * `testid`s `preview-subject` / `preview-content` stay stable for the tests.
 */
export default function AlertMessagePreview(properties: {
    t: Translate;
    kind: 'email' | 'sms' | 'push';
    language?: string;
    /** The alarm the sample shows (`deviceMoving`, …) when the rule is "the device raised an alarm itself". */
    deviceAlarm?: string;
    /** The alarms of the rule: the preview offers one switch each, since a real alert names just the one that fired. */
    deviceAlarms?: string[];
    onDeviceAlarmChoose?: (alarm: string) => void;
    subject?: string;
    /** Push only: the notification's headline (the rule name when the action has no title of its own). */
    title?: string;
    content?: string;
}) {
    const { t } = properties;
    const Icon = properties.kind === 'email' ? Mail : properties.kind === 'push' ? Bell : MessageSquare;

    return (
        <div className="flex min-w-0 flex-col self-start overflow-hidden rounded-lg border bg-background shadow-sm">
            <div className="flex items-center gap-2 border-b bg-muted/50 px-3 py-1.5 text-xs text-muted-foreground">
                <Icon aria-hidden className="h-3.5 w-3.5" />
                <span className="font-semibold uppercase tracking-wide">
                    {t(`${ALERT_TEXT_NAMESPACE}preview`)}
                </span>
            </div>
            <div className="flex min-w-0 flex-col gap-2 p-3 text-sm">
                {properties.deviceAlarms && properties.deviceAlarms.length > 1 && properties.onDeviceAlarmChoose && (
                    <div className="flex min-w-0 flex-wrap items-center gap-1.5 border-b pb-2" role="group" aria-label={t(`${ALERT_TEXT_NAMESPACE}previewFor`)}>
                        <span className="text-xs text-muted-foreground">{t(`${ALERT_TEXT_NAMESPACE}previewFor`)}</span>
                        {properties.deviceAlarms.map((alarm) => {
                            const label = correctedMeasurementLabel(((MeasurementTypeI18N as any)[(properties.language ?? 'de').startsWith('en') ? 'en' : 'de']?.[alarm] ?? alarm) as string);
                            const shown = alarm === properties.deviceAlarm;

                            return (
                                <button
                                    key={alarm}
                                    type="button"
                                    aria-pressed={shown}
                                    data-preview-alarm={alarm}
                                    className={`rounded-full border px-2 py-0.5 text-xs ${shown ? 'border-primary bg-primary/10 font-medium text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
                                    onClick={() => properties.onDeviceAlarmChoose?.(alarm)}
                                >
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                )}
                {properties.kind === 'email' && (
                    <div className="min-w-0 break-words border-b pb-2 font-semibold">
                        <HighlightedTemplate template={properties.subject} testId="preview-subject" language={properties.language} deviceAlarm={properties.deviceAlarm} />
                    </div>
                )}
                {properties.kind === 'push' ? (
                    <PhonePushPreview
                        t={t}
                        title={properties.title}
                        content={properties.content}
                        language={properties.language}
                        deviceAlarm={properties.deviceAlarm}
                    />
                ) : (
                    <div className="min-w-0 whitespace-pre-wrap break-words" data-testid="preview-content">
                        <HighlightedTemplate template={properties.content} language={properties.language} deviceAlarm={properties.deviceAlarm} />
                    </div>
                )}
                <p className="border-t pt-2 text-xs text-muted-foreground">{t(`${ALERT_TEXT_NAMESPACE}sampleData`)}</p>
            </div>
        </div>
    );
}
